import type { CSSProperties, HTMLAttributes } from "react";
import { hitInset } from "./hit";
import { BUTTON_RESET, pressClass } from "./press";
import { scaledFont } from "@/styles/scaledFont";

export interface TabItem {
  /** Stable key, reported back to `onChange`. */
  id: string;
  /** The tab's copy — always `t('…')`, never a literal. */
  label: string;
}

export interface TabsProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  items: ReadonlyArray<TabItem>;
  /** The active tab's `id`. */
  value: string;
  onChange: (id: string) => void;
}

const LIST: CSSProperties = {
  display: "flex",
  gap: "var(--space-6)",
  borderBottom: "1px solid var(--d-line)",
};

/* `margin-bottom: -1` pulls each tab's own underline onto the strip's
 * hairline, so the active one replaces that pixel instead of sitting under
 * it. The inactive tabs keep a transparent line of the same width — without
 * it every label would jump 1px when the selection moves. */
const TAB_HEIGHT = 31; // 18 line + 12 underline gap + 1 underline
const HIT_Y = hitInset(TAB_HEIGHT);
/* The gap to the next tab is 24, so each side may claim 12 of it. */
const HIT_X = 12;

/* DUE-419: the tap area grows to 44 with padding and an equal negative
 * margin on the button. The label and its underline live on an inner span
 * (the underline is that span's border), so the button's extra padding can
 * neither move the underline nor stretch it past the label. */
const TAB: CSSProperties = {
  ...BUTTON_RESET,
  font: scaledFont("600 13px/18px var(--font-body)"),
  letterSpacing: ".01em",
  padding: `${HIT_Y}px ${HIT_X}px`,
  margin: `${-HIT_Y}px ${-HIT_X}px ${-1 - HIT_Y}px`,
};

const LABEL: CSSProperties = {
  display: "block",
  padding: "0 2px var(--space-3)",
};
/**
 * The in-page tab strip — You's Chart / Saved / Settings, a reading's
 * chapters. Not the app's tab bar, which is a floating glass capsule and lives
 * in `components/today/v2`.
 *
 * Ported from the tab strip in
 * `docs/design/handoff/design/templates/you/you-screen.jsx`.
 */
export function Tabs({
  items,
  value,
  onChange,
  className,
  style,
  ...rest
}: TabsProps): JSX.Element {
  return (
    <div
      role="tablist"
      className={className}
      style={{ ...LIST, ...style }}
      {...rest}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={pressClass()}
            onClick={() => onChange(item.id)}
            style={{
              ...TAB,
              color: active ? "var(--d-fg)" : "var(--d-fg-3)",
            }}
          >
            <span
              style={{
                ...LABEL,
                borderBottom: active
                  ? "1px solid var(--d-fg)"
                  : "1px solid transparent",
              }}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
